import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OVERALL = {
  ok: { label: 'Tout fonctionne', className: 'bg-emerald-100 text-emerald-700' },
  warning: { label: 'À vérifier', className: 'bg-amber-100 text-amber-700' },
  critical: { label: 'Critique', className: 'bg-red-100 text-red-700' },
};

export const HealthCheckPanel = () => {
  const [reports, setReports] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/agents/health-reports`, { withCredentials: true });
      setReports(res.data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/agents/health-check`, {}, { withCredentials: true });
      setReports(prev => [res.data, ...prev]);
      toast.success(`Contrôle terminé : ${OVERALL[res.data.overall]?.label} — rapport envoyé par email`);
    } catch (e) {
      toast.error("Erreur lors du contrôle de santé");
    } finally {
      setRunning(false);
    }
  };

  const latest = reports[0];

  return (
    <Card data-testid="health-check-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#1E3A8A]" /> Agent Santé du site
          </CardTitle>
          <p className="text-xs text-[#6B7280] mt-1">Vérification automatique le 30 de chaque mois + rapport par email à l'admin</p>
        </div>
        <Button size="sm" className="bg-[#1E3A8A]" onClick={runNow} disabled={running} data-testid="run-health-check-btn">
          {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
          {running ? 'Contrôle en cours…' : 'Lancer le contrôle maintenant'}
        </Button>
      </CardHeader>
      <CardContent>
        {!latest ? (
          <p className="text-sm text-[#6B7280]" data-testid="health-no-report">Aucun rapport pour le moment. Le premier sera généré le 30 du mois ou manuellement.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3" data-testid="health-latest-summary">
              <Badge className={OVERALL[latest.overall]?.className}>{OVERALL[latest.overall]?.label}</Badge>
              <span className="text-sm text-[#4B5563]">Dernier contrôle : {new Date(latest.created_at).toLocaleString('fr-FR')} ({latest.trigger === 'manual' ? 'manuel' : 'planifié'})</span>
              <span className="text-xs text-[#6B7280]">Email : {latest.email_status}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(latest.checks || {}).map(([name, r]) => (
                <div key={name} className={`flex items-start gap-2 p-3 rounded-md border text-sm ${r.status === 'ok' ? 'bg-emerald-50/50 border-emerald-100' : 'bg-red-50 border-red-200'}`} data-testid="health-check-row">
                  {r.status === 'ok' ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium text-[#111827]">{name}</p>
                    <p className="text-xs text-[#6B7280] truncate">{r.detail} · {r.ms} ms</p>
                  </div>
                </div>
              ))}
            </div>
            {latest.stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {[
                  ['Inscrits 30j', latest.stats.new_users], ['Chantiers 30j', latest.stats.new_jobs],
                  ['Candidatures 30j', latest.stats.new_applications], ['Messages 30j', latest.stats.messages],
                ].map(([l, v]) => (
                  <div key={l} className="p-2 rounded-md bg-[#F9FAFB]">
                    <p className="text-lg font-bold text-[#1E3A8A]">{v}</p>
                    <p className="text-[10px] text-[#6B7280]">{l}</p>
                  </div>
                ))}
              </div>
            )}
            {reports.length > 1 && (
              <div className="text-xs text-[#6B7280] flex flex-wrap gap-2 items-center">
                Historique :
                {reports.slice(1, 7).map(r => (
                  <Badge key={r.id} variant="outline" className={OVERALL[r.overall]?.className}>{new Date(r.created_at).toLocaleDateString('fr-FR')}</Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
